import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, LanguageContext, API, formatTimeWithEnglishNumerals } from '../shared';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  FileText, Package, Plus, Users,
  CheckCircle
} from 'lucide-react';
import AgencyFilter from './AgencyFilter';
import TravelGroupsWidget from './TravelGroupsWidget';

const Dashboard = ({ setActiveTab }) => {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedAgency, setSelectedAgency] = useState(null);
  // 🆕 Pending approvals counters — drives the alert banner below.
  // Polled every 20s so the accountant/admin sees new pending items
  // appear shortly after staff submits them. Staff only sees their own.
  const [pending, setPending] = useState({
    tickets: 0, organized_trips: 0, daily_operations: 0, trip_payments: 0,
  });
  // 🆕 Quick installments-by-agency summary for the admin alert card below.
  const [installmentsSummary, setInstallmentsSummary] = useState(null);
  // 🆕 Compact financial-status card under the hero banner. Lights up red
  // and shows a "حوّل النقدية" CTA when the balance crosses 1,000,000 DZD.
  const [financialStatus, setFinancialStatus] = useState(null);
  const { t } = useContext(LanguageContext);
  const { user } = useContext(AuthContext);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const params = selectedAgency ? `?agency_id=${selectedAgency}` : '';
      const response = await axios.get(`${API}/dashboard${params}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPending = async () => {
    try {
      const r = await axios.get(`${API}/pending-approvals/summary`);
      setPending(r.data || { tickets: 0, organized_trips: 0, daily_operations: 0, trip_payments: 0 });
    } catch (_e) {
      // Silently ignore — banner just stays hidden if the endpoint is
      // unreachable.
    }
  };

  // 🆕 Quick fetch of installments-by-agency grand totals for the
  // dashboard alert. Visible to all roles; backend already scopes the
  // data by role (staff sees only their own agency).
  const fetchInstallmentsSummary = async () => {
    try {
      const r = await axios.get(`${API}/installment-reports/by-agency`);
      setInstallmentsSummary(r.data || null);
    } catch (_e) {
      // ignore — alert just stays hidden
    }
  };

  // 🆕 Compact "Financial Status" widget. Reuses the same endpoint as the
  // Financial Management page; backend scopes per role (staff/manager →
  // own agency only, super_admin/accountant → all agencies).
  const fetchFinancialStatus = async () => {
    try {
      const r = await axios.get(`${API}/agencies-balances/all`);
      setFinancialStatus(r.data || null);
    } catch (_e) {
      // ignore — widget just stays hidden
    }
  };

  useEffect(() => {
    fetchStats();
    fetchPending();
    fetchInstallmentsSummary();
    fetchFinancialStatus();
    const interval = setInterval(() => {
      fetchStats();
      fetchFinancialStatus();
    }, 300000);
    // Poll pending more often (20s) — this is the alert loop.
    const pendingInterval = setInterval(fetchPending, 20000);
    return () => {
      clearInterval(interval);
      clearInterval(pendingInterval);
    };
  }, [selectedAgency]);

  const getAgencyFilterText = () => {
    if (!selectedAgency) return 'جميع الوكالات';
    return 'الوكالة المحددة';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      {/* Agency Filter for Super Admin and General Accountant */}
      {['super_admin', 'general_accountant'].includes(user?.role) && (
        <Card className="bg-brand-50 border-brand-200">
          <CardContent className="p-4">
            <AgencyFilter
              selectedAgency={selectedAgency}
              onAgencyChange={setSelectedAgency}
              showAllOption={true}
            />
          </CardContent>
        </Card>
      )}

      {/* Welcome Section — premium hero banner with subtle pattern.
          Larger headline, refined typography, gold accent strip. */}
      <div className="relative overflow-hidden bg-gradient-to-bl from-brand-700 via-brand-600 to-brand-800 rounded-3xl p-8 text-white shadow-2xl shadow-brand-900/30">
        {/* Decorative gold corner accents */}
        <div className="absolute top-0 left-0 w-40 h-40 bg-yellow-400/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-56 h-56 bg-amber-300/10 rounded-full blur-3xl translate-x-1/3 translate-y-1/3 pointer-events-none" />
        <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-l from-transparent via-yellow-400 to-transparent" />

        <div className="relative flex justify-between items-start gap-6 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <h2 className="text-4xl sm:text-5xl font-extrabold mb-3 leading-tight tracking-tight">
              🏠 {t('quickStats')}
            </h2>
            <p className="text-xl text-brand-50 font-medium">
              مرحباً بك في نظام إدارة وكالات صنهاجة للسفر
            </p>
            {['super_admin', 'general_accountant'].includes(user?.role) && (
              <p className="text-base text-brand-200 mt-2 font-semibold">
                📊 عرض بيانات: {getAgencyFilterText()}
              </p>
            )}
          </div>
          <div className="text-right bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-4 border border-white/20">
            <p className="text-2xl font-bold">
              {new Date().toLocaleDateString('ar-SA', {
                weekday: 'long',
                day: 'numeric',
                month: 'long'
              })}
            </p>
            <p className="text-3xl font-extrabold text-yellow-300 mt-1 tracking-wider tabular-nums">
              {formatTimeWithEnglishNumerals(new Date(), {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        </div>
      </div>

      {/* 🆕 Compact Financial-Status strip (sits right under the hero).
          Shows the agency cash balance in a small, color-different chip
          and turns RED with a strong "حوّل النقدية" CTA the moment the
          balance crosses the per-agency threshold (default 1,000,000
          DZD, customizable from Agency Settings). */}
      {financialStatus?.totals && (() => {
        const t = financialStatus.totals;
        const bal = Number(t.current_balance || 0);
        const isMultiAgency = ['super_admin', 'general_accountant'].includes(user?.role);

        // Pick the threshold to compare the displayed total against.
        // - Single-agency view (staff, services_manager, OR admin with the
        //   "all agencies" filter showing exactly one row): use that agency's
        //   own threshold.
        // - Multi-agency view: use the lowest threshold among visible
        //   agencies so even a small branch overflowing triggers the alert.
        let threshold = 1_000_000;
        let overflowingAgencies = [];
        if (Array.isArray(financialStatus.agencies) && financialStatus.agencies.length > 0) {
          const thresholds = financialStatus.agencies
            .map(a => Number(a.cash_transfer_threshold || 1_000_000));
          threshold = Math.min(...thresholds);
          overflowingAgencies = financialStatus.agencies.filter(
            a => (a.current_balance || 0) >= (a.cash_transfer_threshold || 1_000_000)
          );
        }
        const needsTransfer = isMultiAgency
          ? overflowingAgencies.length > 0
          : bal >= threshold;

        return (
          <div
            className={
              `flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-xl border text-sm shadow-sm transition ` +
              (needsTransfer
                ? 'bg-rose-50 border-rose-300 text-rose-900 animate-pulse'
                : 'bg-teal-50 border-teal-300 text-teal-900')
            }
            data-testid="financial-status-strip"
          >
            <span className="text-lg leading-none">
              {needsTransfer ? '🚨' : '💰'}
            </span>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 flex-1 min-w-[200px]">
              <span className="font-bold">الحالة المالية:</span>
              <span
                className={
                  'tabular-nums font-extrabold text-lg ' +
                  (needsTransfer ? 'text-rose-700' : 'text-teal-700')
                }
                data-testid="financial-status-balance"
                title="الرصيد الحالي بعد خصم التحويلات المؤكَّدة والمصاريف"
              >
                {bal.toLocaleString('en-US', { maximumFractionDigits: 0 })} دج
              </span>
              <span className="text-xs text-gray-600 hidden sm:inline">
                • مداخيل {Number(t.total_revenue || 0).toLocaleString()} دج
                • محوَّل {Number(t.total_transferred || 0).toLocaleString()} دج
                • مصاريف {Number(t.total_expenses || 0).toLocaleString()} دج
              </span>
              {/* In multi-agency mode list ALL the branches over threshold */}
              {isMultiAgency && overflowingAgencies.map(a => (
                <span
                  key={a.agency_id}
                  className="text-xs bg-rose-200 text-rose-900 px-2 py-0.5 rounded-full font-bold whitespace-nowrap"
                  data-testid={`financial-status-overflowing-${a.agency_id}`}
                  title={`الحد المحدَّد: ${Number(a.cash_transfer_threshold || 1_000_000).toLocaleString()} دج`}
                >
                  🏢 {a.agency_name}: {Number(a.current_balance || 0).toLocaleString()} دج
                  {a.is_subagent && (
                    <span className="mr-1 px-1 py-0.5 rounded bg-amber-200 text-amber-900 text-[9px]">B2B</span>
                  )}
                </span>
              ))}
              {/* In single-agency mode, surface the configured threshold so
                  the user understands WHY the alert turned red. */}
              {!isMultiAgency && (
                <span
                  className="text-[11px] text-gray-500 italic"
                  title="يمكن تعديل الحدّ من إعدادات الوكالة"
                >
                  (الحدّ المُعيَّن: {threshold.toLocaleString()} دج)
                </span>
              )}
            </div>
            {needsTransfer && (
              <>
                <span
                  className="text-xs font-bold text-rose-800 bg-rose-100 px-2 py-1 rounded border border-rose-300"
                  data-testid="financial-status-warning"
                >
                  ⚠️ تجاوز الحدّ المُعيَّن — يُنصح بتحويل النقدية للإدارة العامة
                </span>
                <button
                  onClick={() => {
                    sessionStorage.setItem('pending-intent', 'pending_balance');
                    setActiveTab('financialManagement');
                  }}
                  className="bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded-md text-xs font-bold shadow transition whitespace-nowrap"
                  data-testid="financial-status-transfer-btn"
                >
                  💸 حوّل النقدية الآن ←
                </button>
              </>
            )}
          </div>
        );
      })()}

      {/* 🏢 PER-AGENCY BALANCE CARDS — admin/accountant only. User request
          (2026-07-20): show every agency's REAL transferable balance side
          by side so we can decide which one exceeded its transfer threshold
          and needs a cash-transfer to HQ. Uses the same unified balance
          numbers as the Financial Management tab — 100% consistent. */}
      {financialStatus && financialStatus.agencies && financialStatus.agencies.length > 1
        && ['super_admin', 'general_accountant'].includes(user?.role)
        && (() => {
          const rows = [...(financialStatus.agencies || [])]
            .filter((a) => !a.is_subagent) // internal branches only in this grid
            .sort((a, b) => Number(b.current_balance || 0) - Number(a.current_balance || 0));
          if (rows.length === 0) return null;
          return (
            <div className="rounded-xl border-2 border-blue-200 bg-blue-50/40 p-3" data-testid="per-agency-balances">
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold text-blue-900 flex items-center gap-2">
                  🏢 رصيد كل وكالة (القابل للتحويل)
                </div>
                <div className="text-xs text-blue-800">
                  اضغط على وكالة لفتح صفحتها المالية
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                {rows.map((a) => {
                  const bal = Number(a.current_balance || 0);
                  const threshold = Number(a.cash_transfer_threshold || 1_000_000);
                  const isOver = bal >= threshold;
                  const percent = threshold > 0 ? Math.min(100, Math.round((bal / threshold) * 100)) : 0;
                  return (
                    <button
                      type="button"
                      key={a.agency_id}
                      onClick={() => { setSelectedAgency(a.agency_id); setActiveTab('financial-management'); }}
                      className={`text-right p-3 rounded-lg border-2 transition hover:shadow-md ${
                        isOver
                          ? 'border-rose-300 bg-rose-50 hover:bg-rose-100'
                          : 'border-emerald-200 bg-white hover:bg-emerald-50'
                      }`}
                      data-testid={`agency-balance-card-${a.agency_id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-bold text-sm text-gray-900 truncate" title={a.agency_name}>
                          🏢 {a.agency_name}
                        </div>
                        {isOver && (
                          <span className="text-[10px] font-bold text-white bg-rose-600 px-1.5 py-0.5 rounded animate-pulse">
                            تحويل!
                          </span>
                        )}
                      </div>
                      <div className={`text-lg font-extrabold ${isOver ? 'text-rose-800' : 'text-emerald-800'}`}>
                        {bal.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} <span className="text-xs font-normal">دج</span>
                      </div>
                      <div className="mt-1.5">
                        <div className="text-[10px] text-gray-600 mb-0.5">
                          الحدّ الأقصى: {threshold.toLocaleString('fr-FR')} دج ({percent}%)
                        </div>
                        <div className="w-full h-1.5 bg-gray-200 rounded overflow-hidden">
                          <div
                            className={`h-full ${isOver ? 'bg-rose-500' : percent > 70 ? 'bg-amber-400' : 'bg-emerald-500'}`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                      {a.agency_city && (
                        <div className="text-[10px] text-gray-500 mt-1">📍 {a.agency_city}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

      {/* 🤝 Subagent vs Branches glance card — only for super_admin/general_accountant.
          Compact stripe under the financial-status widget summarising how
          many partner-agencies (B2B) exist and how much cash they're sitting on. */}
      {financialStatus && financialStatus.agencies && financialStatus.agencies.length > 1
        && ['super_admin', 'general_accountant'].includes(user?.role)
        && (() => {
          const subs = financialStatus.agencies.filter(a => a.is_subagent);
          const branches = financialStatus.agencies.filter(a => !a.is_subagent);
          if (subs.length === 0) return null;
          const subBalance = subs.reduce((sum, a) => sum + Number(a.current_balance || 0), 0);
          const subRevenue = subs.reduce((sum, a) => sum + Number(a.total_revenue || 0), 0);
          return (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex flex-wrap items-center gap-3 text-sm"
              data-testid="subagent-glance-strip"
            >
              <span className="font-bold text-amber-900">🤝 الوكلاء المُناوِلون</span>
              <span className="text-amber-800">
                {subs.length} وكيل / {branches.length} فرع داخلي
              </span>
              <span className="text-xs text-amber-700">
                • مداخيلهم: {subRevenue.toLocaleString()} دج
                • رصيدهم الحالي: {subBalance.toLocaleString()} دج
              </span>
              <button
                onClick={() => setActiveTab('subagents-management')}
                className="ml-auto bg-amber-600 hover:bg-amber-700 text-white text-xs px-3 py-1 rounded-md font-bold"
                data-testid="subagent-glance-cta"
              >
                إدارة الوكلاء ←
              </button>
            </div>
          );
        })()}

      {/* 🚨 PENDING APPROVALS ALERT BANNER
          Visible to staff (showing only their own pending items) AND to
          accountant/admin (showing the agency-wide queue). Pulsing red-amber
          gradient grabs attention; click any chip to jump to the relevant
          screen. Total pending = receipts/tickets/trips/installments.
          When everything is approved, the banner is hidden. */}
      {(() => {
        const totalPending =
          (pending.tickets || 0) +
          (pending.organized_trips || 0) +
          (pending.trip_payments || 0) +
          (pending.daily_operations || 0);
        if (totalPending <= 0) return null;
        const isApprover = ['super_admin', 'general_accountant'].includes(user?.role);
        const titleText = isApprover
          ? `🚨 لديك ${totalPending} وصل بانتظار التأكيد أو الرفض`
          : `⏳ لديك ${totalPending} وصل قيد المراجعة من قِبَل المحاسب`;
        const subText = isApprover
          ? 'يرجى المراجعة فوراً — هذه الوصولات لا تُحسب في الحالة المالية حتى الاعتماد'
          : 'تنتظر دفعاتك / تذاكرك اعتماد المحاسب أو المدير لتظهر في الحالة المالية';
        return (
          <div
            className="relative overflow-hidden rounded-2xl border-2 border-amber-400 shadow-xl shadow-amber-200/50 bg-gradient-to-l from-amber-50 via-yellow-100 to-amber-50"
            data-testid="pending-approvals-banner"
          >
            {/* Animated highlight strip on top */}
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-l from-amber-500 via-yellow-400 to-amber-500 animate-pulse" />
            {/* Subtle decorative glow */}
            <div className="absolute -top-10 left-1/3 w-64 h-32 bg-yellow-300/30 rounded-full blur-3xl pointer-events-none" />

            <div className="relative flex items-center gap-5 p-5 flex-wrap">
              {/* Big bouncing bell */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <span className="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-30"></span>
                  <span className="relative inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-yellow-600 text-white text-3xl shadow-lg shadow-amber-300/60">
                    🔔
                  </span>
                </div>
              </div>

              <div className="flex-1 min-w-[240px]">
                <h3 className="text-xl sm:text-2xl font-extrabold text-amber-900 leading-tight">
                  {titleText}
                </h3>
                <p className="text-sm sm:text-base text-amber-800 mt-1 font-medium">
                  {subText}
                </p>
              </div>

              {/* Clickable chips per category — staff also see them
                  (filtered to their own items by the backend).
                  Trip payments get a stronger red-pulsing treatment
                  because they directly affect cash-flow / agency balance
                  and are the highest-priority approvals.
                  🆕 Each chip writes a `pending-intent` flag to sessionStorage
                  BEFORE switching tabs so the target page auto-filters
                  to "بانتظار الاعتماد فقط" instead of dumping everything. */}
              <div className="flex flex-wrap items-center gap-2">
                {pending.trip_payments > 0 && (
                  <button
                    onClick={() => {
                      sessionStorage.setItem('pending-intent', 'trip_payments');
                      setActiveTab('organized-trips');
                    }}
                    className="relative inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-l from-red-600 to-rose-700 hover:from-red-700 hover:to-rose-800 text-white rounded-2xl border-2 border-red-400 font-extrabold shadow-lg shadow-red-300/60 hover:shadow-xl transition-all text-base animate-pulse hover:animate-none ring-2 ring-red-300/50 ring-offset-2 ring-offset-amber-50"
                    data-testid="pending-chip-trip-payments"
                  >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-red-700 text-lg font-extrabold shadow-inner">
                      {pending.trip_payments}
                    </span>
                    💵 دفعات رحلات منظمة
                    <span className="hidden sm:inline text-xs bg-yellow-300 text-red-900 px-1.5 py-0.5 rounded-md tracking-wide">عاجل</span>
                  </button>
                )}
                {pending.tickets > 0 && (
                  <button
                    onClick={() => {
                      sessionStorage.setItem('pending-intent', 'tickets');
                      setActiveTab('tickets');
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-amber-50 text-amber-900 rounded-full border-2 border-amber-300 font-bold shadow-sm hover:shadow-md transition-all text-sm"
                    data-testid="pending-chip-tickets"
                  >
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-sky-500 text-white text-sm font-extrabold">
                      {pending.tickets}
                    </span>
                    ✈️ تذاكر
                  </button>
                )}
                {pending.organized_trips > 0 && (
                  <button
                    onClick={() => {
                      sessionStorage.setItem('pending-intent', 'trips');
                      setActiveTab('organized-trips');
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-amber-50 text-amber-900 rounded-full border-2 border-amber-300 font-bold shadow-sm hover:shadow-md transition-all text-sm"
                    data-testid="pending-chip-trips"
                  >
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-white text-sm font-extrabold">
                      {pending.organized_trips}
                    </span>
                    🗺️ رحلات
                  </button>
                )}
                {pending.daily_operations > 0 && (
                  <button
                    onClick={() => {
                      sessionStorage.setItem('pending-intent', 'daily_operations');
                      setActiveTab('dailyOperations');
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-amber-50 text-amber-900 rounded-full border-2 border-amber-300 font-bold shadow-sm hover:shadow-md transition-all text-sm"
                    data-testid="pending-chip-daily-ops"
                  >
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-white text-sm font-extrabold">
                      {pending.daily_operations}
                    </span>
                    📋 عمليات يومية
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Detailed Analytics Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Quick Actions — premium gold-accented design.
            Bigger labels, hover lift, gradient ring, descriptive subtitle.
            Buttons match the Royal Gold theme (yellow/black). */}
        <Card className="border-2 border-amber-200/60 shadow-xl shadow-amber-100/30 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-l from-amber-400 via-yellow-500 to-amber-400" />
          <CardHeader className="bg-gradient-to-bl from-amber-50 to-white pb-4">
            <CardTitle className="flex items-center gap-3 text-2xl font-extrabold text-amber-900">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-600 text-white shadow-md shadow-amber-200">
                <Plus className="h-7 w-7" strokeWidth={2.5} />
              </span>
              <span>الإجراءات السريعة</span>
            </CardTitle>
            <p className="text-base text-amber-700/80 mt-1 pr-15 mr-1">
              الوصول السريع لأكثر العمليات استعمالاً
            </p>
          </CardHeader>
          <CardContent className="space-y-3 p-5">
            <button
              onClick={() => setActiveTab('clients')}
              className="group w-full flex items-center justify-between gap-4 p-4 bg-gradient-to-l from-brand-50 to-white hover:from-brand-100 hover:to-brand-50 rounded-xl border border-brand-200/70 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 text-right"
              data-testid="quick-add-client"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-100 text-brand-700 group-hover:bg-brand-600 group-hover:text-white transition-colors">
                  <Users className="h-6 w-6" strokeWidth={2} />
                </span>
                <span className="text-brand-400 text-lg font-bold group-hover:translate-x-[-4px] transition-transform">←</span>
              </div>
              <span className="text-lg font-bold text-brand-900">➕ إضافة عميل جديد</span>
            </button>

            <button
              onClick={() => setActiveTab('tickets')}
              className="group w-full flex items-center justify-between gap-4 p-4 bg-gradient-to-l from-sky-50 to-white hover:from-sky-100 hover:to-sky-50 rounded-xl border border-sky-200/70 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 text-right"
              data-testid="quick-new-ticket"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-100 text-sky-700 group-hover:bg-sky-600 group-hover:text-white transition-colors">
                  <FileText className="h-6 w-6" strokeWidth={2} />
                </span>
                <span className="text-sky-400 text-lg font-bold group-hover:translate-x-[-4px] transition-transform">←</span>
              </div>
              <span className="text-lg font-bold text-sky-900">✈️ تذكرة طيران جديدة</span>
            </button>

            <button
              onClick={() => setActiveTab('organized-trips')}
              className="group w-full flex items-center justify-between gap-4 p-4 bg-gradient-to-l from-emerald-50 to-white hover:from-emerald-100 hover:to-emerald-50 rounded-xl border border-emerald-200/70 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 text-right"
              data-testid="quick-new-trip"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                  <Package className="h-6 w-6" strokeWidth={2} />
                </span>
                <span className="text-emerald-400 text-lg font-bold group-hover:translate-x-[-4px] transition-transform">←</span>
              </div>
              <span className="text-lg font-bold text-emerald-900">🗺️ رحلة منظمة جديدة</span>
            </button>

            <button
              onClick={() => setActiveTab('dailyOperations')}
              className="group w-full flex items-center justify-between gap-4 p-4 bg-gradient-to-l from-orange-50 to-white hover:from-orange-100 hover:to-orange-50 rounded-xl border border-orange-200/70 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 text-right"
              data-testid="quick-new-daily-operation"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-orange-100 text-orange-700 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                  <FileText className="h-6 w-6" strokeWidth={2} />
                </span>
                <span className="text-orange-400 text-lg font-bold group-hover:translate-x-[-4px] transition-transform">←</span>
              </div>
              <span className="text-lg font-bold text-orange-900">📋 عملية يومية جديدة</span>
            </button>
          </CardContent>
        </Card>

        {/* Travel Groups Widget */}
        <TravelGroupsWidget onNavigate={setActiveTab} />
      </div>

      {/* 🆕 Installments by Agency — quick admin alert + click-through.
          Visible whenever there is at least one tracked installment receipt
          in scope. The KPI lights up red when any payment is overdue. */}
      {installmentsSummary?.grand_totals?.receipts_count > 0 && (
        <Card
          className={`border-2 ${installmentsSummary.grand_totals.overdue_count > 0 ? 'border-red-300 bg-red-50' : 'border-blue-300 bg-blue-50'} cursor-pointer hover:shadow-lg transition`}
          onClick={() => setActiveTab('installments-by-agency')}
          data-testid="dashboard-installments-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="text-3xl">{installmentsSummary.grand_totals.overdue_count > 0 ? '🔴' : '📊'}</div>
                <div>
                  <div className="font-bold text-lg" data-testid="dashboard-installments-title">
                    {installmentsSummary.grand_totals.overdue_count > 0
                      ? '⚠️ يوجد أقساط متأخرة — اضغط لعرض الوكالات'
                      : '📊 الأقساط حسب الوكالة — عرض الحالة'}
                  </div>
                  <div className="text-xs text-gray-700 mt-0.5">
                    {installmentsSummary.agencies?.length || 0} وكالة •{' '}
                    {installmentsSummary.grand_totals.clients_count} عميل •{' '}
                    {installmentsSummary.grand_totals.receipts_count} وصل
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-white px-3 py-1.5 rounded shadow-sm">
                  <div className="text-gray-600">إجمالي</div>
                  <div className="font-bold">{Number(installmentsSummary.grand_totals.total_amount || 0).toLocaleString()} دج</div>
                </div>
                <div className="bg-green-100 px-3 py-1.5 rounded shadow-sm">
                  <div className="text-green-700">مُحصَّل</div>
                  <div className="font-bold text-green-800">{Number(installmentsSummary.grand_totals.paid_amount || 0).toLocaleString()} دج</div>
                </div>
                <div className={`${installmentsSummary.grand_totals.overdue_count > 0 ? 'bg-red-100' : 'bg-gray-100'} px-3 py-1.5 rounded shadow-sm`}>
                  <div className={installmentsSummary.grand_totals.overdue_count > 0 ? 'text-red-700' : 'text-gray-600'}>متأخر</div>
                  <div className={`font-bold ${installmentsSummary.grand_totals.overdue_count > 0 ? 'text-red-800' : 'text-gray-700'}`}>
                    {Number(installmentsSummary.grand_totals.overdue_amount || 0).toLocaleString()} دج
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alert Banner for Important Actions */}
      {(stats.unpaid_invoices > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-5 w-5 text-amber-400" />
            </div>
            <div className="mr-3 flex-1">
              <h3 className="text-sm font-medium text-amber-800 text-right">
                ⚠️ تنبيه: يوجد {stats.unpaid_invoices} فاتورة غير مسددة تحتاج للمتابعة
              </h3>
              <div className="mt-2">
                <button
                  onClick={() => setActiveTab('invoices')}
                  className="text-sm bg-amber-100 hover:bg-amber-200 text-amber-800 px-3 py-1 rounded-md transition-colors"
                >
                  عرض الفواتير المعلقة ←
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
